import { z } from 'zod';
import { canonicalNumber } from './text-matching';

export const QUESTION_TYPES = ['choice', 'text', 'order', 'match'] as const;

export type QuestionType = (typeof QUESTION_TYPES)[number];

export const QUESTION_STATUSES = ['draft', 'published', 'archived'] as const;

export type QuestionStatus = (typeof QUESTION_STATUSES)[number];

const itemIdSchema = z.string().min(1).max(64);

const itemSchema = z
  .object({
    id: itemIdSchema,
    text: z.string().trim().min(1).max(500).optional(),
    imageKey: z.string().min(1).max(300).optional(),
    imageAlt: z.string().trim().min(1).max(200).optional(),
  })
  .refine((item) => item.text !== undefined || item.imageKey !== undefined, {
    message: 'An item needs text or an image',
  })
  .refine((item) => item.imageAlt === undefined || item.imageKey !== undefined, {
    message: 'An image description needs an image',
  });

function hasUniqueIds(items: readonly { readonly id: string }[]): boolean {
  return new Set(items.map((item) => item.id)).size === items.length;
}

const itemListSchema = (min: number, max: number) =>
  z
    .array(itemSchema)
    .min(min)
    .max(max)
    .refine(hasUniqueIds, { message: 'Item ids must be unique' });

export const choiceContentSchema = z.object({
  type: z.literal('choice'),
  options: itemListSchema(2, 6),
});

export const textContentSchema = z.object({
  type: z.literal('text'),
  inputMode: z.enum(['text', 'numeric']),
  gap: z.object({ before: z.string().max(500), after: z.string().max(500) }).nullable(),
  maxLength: z.int().min(1).max(200),
});

export const orderContentSchema = z.object({
  type: z.literal('order'),
  items: itemListSchema(2, 8),
});

export const matchContentSchema = z.object({
  type: z.literal('match'),
  left: itemListSchema(2, 8),
  right: itemListSchema(2, 8),
});

export const questionContentSchema = z.discriminatedUnion('type', [
  choiceContentSchema,
  textContentSchema,
  orderContentSchema,
  matchContentSchema,
]);

const pairSchema = z.object({ left: itemIdSchema, right: itemIdSchema });

export const answerKeySchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('choice'), correctOptionId: itemIdSchema }),
  z.object({
    type: z.literal('text'),
    accepted: z.array(z.string().trim().min(1).max(200)).min(1).max(20),
    caseSensitive: z.boolean(),
  }),
  z.object({ type: z.literal('order'), order: z.array(itemIdSchema).min(2).max(8) }),
  z.object({ type: z.literal('match'), pairs: z.array(pairSchema).min(2).max(8) }),
]);

export const answerResponseSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('choice'), optionId: itemIdSchema }),
  z.object({ type: z.literal('text'), value: z.string().max(200) }),
  z.object({ type: z.literal('order'), order: z.array(itemIdSchema).max(8) }),
  z.object({ type: z.literal('match'), pairs: z.array(pairSchema).max(8) }),
]);

export type QuestionContent = z.infer<typeof questionContentSchema>;
export type AnswerKey = z.infer<typeof answerKeySchema>;
export type AnswerResponse = z.infer<typeof answerResponseSchema>;
export type AnswerPair = z.infer<typeof pairSchema>;

function isPermutationOf(candidate: readonly string[], ids: readonly string[]): boolean {
  return (
    candidate.length === ids.length &&
    new Set(candidate).size === candidate.length &&
    candidate.every((id) => ids.includes(id))
  );
}

function matchIssues(
  left: readonly { readonly id: string }[],
  right: readonly { readonly id: string }[],
  pairs: readonly AnswerPair[],
): string[] {
  const leftIds = left.map((item) => item.id);
  const rightIds = right.map((item) => item.id);
  const pairedLeft = pairs.map((pair) => pair.left);
  const pairedRight = pairs.map((pair) => pair.right);
  const issues: string[] = [];
  if (!isPermutationOf(pairedLeft, leftIds)) {
    issues.push('Every left item must be paired exactly once');
  }
  if (new Set(pairedRight).size !== pairedRight.length) {
    issues.push('A right item can only be used once');
  }
  if (!pairedRight.every((id) => rightIds.includes(id))) {
    issues.push('Pairs refer to a right item that does not exist');
  }
  return issues;
}

export function questionDefinitionIssues(content: QuestionContent, key: AnswerKey): string[] {
  if (content.type === 'choice' && key.type === 'choice') {
    return content.options.some((option) => option.id === key.correctOptionId)
      ? []
      : ['The correct option is not one of the options'];
  }
  if (content.type === 'text' && key.type === 'text') {
    const issues: string[] = [];
    if (
      content.inputMode === 'numeric' &&
      key.accepted.some((value) => canonicalNumber(value) === null)
    ) {
      issues.push('Numeric questions only accept numbers as answers');
    }
    if (key.accepted.some((value) => value.length > content.maxLength)) {
      issues.push('An accepted answer is longer than the input allows');
    }
    return issues;
  }
  if (content.type === 'order' && key.type === 'order') {
    const itemIds = content.items.map((item) => item.id);
    return isPermutationOf(key.order, itemIds)
      ? []
      : ['The correct order must contain every item exactly once'];
  }
  if (content.type === 'match' && key.type === 'match') {
    return matchIssues(content.left, content.right, key.pairs);
  }
  return ['The answer does not match the question type'];
}

export const IMAGE_REQUEST_STATUSES = ['pending', 'done', 'failed', 'skipped'] as const;

export type ImageRequestStatus = (typeof IMAGE_REQUEST_STATUSES)[number];

export const AUTO_IMAGE = 'auto';
